import { Component, computed, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  LicensePlateHolderDto,
  LicensePlateWalletsDto,
  ParkerWalletDto,
  ParkingSpaceDto,
  WalletEntryDto,
  WalletEntryType,
  WalletService,
} from '../../../api/gen';

const ENTRY_LABELS: Record<WalletEntryType, string> = {
  CoinCredit: 'Coin credit',
  Redemption: 'Spent at kiosk',
  TopUp: 'Top-up',
  TransferIn: 'Transfer in',
  TransferOut: 'Transfer out',
  Adjustment: 'Adjustment',
};

/** Cents → "€ 1,50" (de-AT, like every receipt the parker gets). */
export function formatCents(cents: number | null | undefined, currency = 'EUR'): string {
  return new Intl.NumberFormat('de-AT', { style: 'currency', currency }).format((cents ?? 0) / 100);
}

/**
 * Parker credit on the plate page: each holder's balance, where it may be spent, and the ledger of
 * when and where it was earned or spent — plus credit earned on this plate by parkers who no longer
 * hold it (credit stays with the parker who earned it when a plate changes owner).
 *
 * Read-only for operator members. Admins and operator admins can set where a parker's credit may be
 * spent; the backend keeps any space outside their scope that is already allowed.
 */
@Component({
  selector: 'app-license-plate-balance',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './license-plate-balance.component.html',
})
export class LicensePlateBalanceComponent {
  private readonly walletApi = inject(WalletService);
  private readonly snackBar = inject(MatSnackBar);

  readonly wallets = input<LicensePlateWalletsDto | null>(null);
  readonly holders = input<LicensePlateHolderDto[]>([]);
  /** Spaces this operator can see — the only ones offered in the editor. */
  readonly spaces = input<ParkingSpaceDto[]>([]);
  readonly canEdit = input(false);

  /** Emitted after a successful save so the page reloads the wallets (one request). */
  readonly changed = output<void>();

  readonly formatCents = formatCents;

  readonly holderWallets = computed(() => this.wallets()?.holderWallets ?? []);
  readonly formerOwnerEntries = computed(() => this.wallets()?.formerOwnerEntries ?? []);

  private readonly spaceNames = computed(() => {
    const map = new Map<string, string>();
    for (const s of this.spaces()) if (s.id && s.name) map.set(s.id, s.name);
    return map;
  });

  // ── Allowed-spaces editor ─────────────────────────────────────────────────
  readonly editingParkerId = signal<string | null>(null);
  readonly draftAllowAll = signal(true);
  readonly draftSpaceIds = signal<Set<string>>(new Set());
  readonly saving = signal(false);

  holderName(parkerId: string | undefined): string {
    const holder = this.holders().find((h) => h.parkerId === parkerId);
    if (!holder) return 'Parker';
    const name = [holder.firstName, holder.lastName].filter(Boolean).join(' ');
    if (name) return name;
    if (holder.email) return holder.email;
    return holder.isRegistered ? 'Registered parker' : 'Guest parker';
  }

  entryLabel(entry: WalletEntryDto): string {
    return entry.type ? ENTRY_LABELS[entry.type] : '—';
  }

  /** The space name the backend resolved (withheld outside the caller's scope), else a neutral label. */
  entrySpace(entry: WalletEntryDto): string {
    if (!entry.parkingSpaceId) return '—';
    return entry.parkingSpaceName ?? this.spaceNames().get(entry.parkingSpaceId) ?? 'Other site';
  }

  /** "All parking spaces", or the visible names plus a count of spaces this operator cannot see. */
  allowedSpacesLabel(wallet: ParkerWalletDto): string {
    if (wallet.allowAllSpaces !== false) return 'All parking spaces';
    const ids = wallet.allowedSpaceIds ?? [];
    if (ids.length === 0) return 'No parking space';
    const names = ids.map((id) => this.spaceNames().get(id)).filter((n): n is string => !!n);
    const hidden = ids.length - names.length;
    return [...names, ...(hidden > 0 ? [`${hidden} other site${hidden > 1 ? 's' : ''}`] : [])].join(', ');
  }

  startEdit(wallet: ParkerWalletDto): void {
    this.editingParkerId.set(wallet.parkerId ?? null);
    this.draftAllowAll.set(wallet.allowAllSpaces !== false);
    // Only spaces this operator can see are editable; the backend keeps the rest untouched.
    const visible = new Set(this.spaces().map((s) => s.id).filter((id): id is string => !!id));
    this.draftSpaceIds.set(new Set((wallet.allowedSpaceIds ?? []).filter((id) => visible.has(id))));
  }

  cancelEdit(): void {
    this.editingParkerId.set(null);
  }

  toggleSpace(spaceId: string): void {
    const next = new Set(this.draftSpaceIds());
    if (next.has(spaceId)) next.delete(spaceId);
    else next.add(spaceId);
    this.draftSpaceIds.set(next);
  }

  save(): void {
    const parkerId = this.editingParkerId();
    if (!parkerId || this.saving()) return;

    this.saving.set(true);
    this.walletApi
      .operatorWalletParkerParkerIdSpacesPut(parkerId, {
        allowAllSpaces: this.draftAllowAll(),
        allowedSpaceIds: this.draftAllowAll() ? [] : [...this.draftSpaceIds()],
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.editingParkerId.set(null);
          this.snackBar.open('Credit spaces saved.', 'OK', { duration: 3000 });
          this.changed.emit();
        },
        error: (err) => {
          this.saving.set(false);
          const detail = typeof err?.error === 'string' ? err.error : 'Could not save the credit spaces.';
          this.snackBar.open(detail, 'Dismiss', { duration: 5000 });
        },
      });
  }
}
